import { formatAppDateTime } from '../utility/dateLocale';
import { Box, Group, TextInput, Divider, Avatar, Text, ActionIcon, Popover, ScrollArea, UnstyledButton, useMantineTheme, Indicator } from '@mantine/core';
import { IconMenu2, IconSearch, IconSettings, IconBell, IconLogout } from '@tabler/icons-react';
import { useAuth } from '../contexts/AuthProvider';
import { parsePhotoUrl } from '../utility/parsers';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { UserEditModal } from './UserEditModal';
import { useNotifications } from '../contexts/NotificationProvider';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';


interface NavbarTopProps {
  onToggleSidebar: () => void;
}

export function NavbarTop({ onToggleSidebar }: NavbarTopProps) {
  const { user, actualEnterprise, setActualEnterprise, logout } = useAuth();
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const navigate = useNavigate();

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [popoverOpened, { toggle: togglePopover, close: closePopover }] = useDisclosure(false);

  const {notifications, atualizar , setViewed } = useNotifications();


    useEffect(() => {
      console.log("USER", user);
      console.log("ENTERPRISE", actualEnterprise);
    }, [user, actualEnterprise]);
  

  // Contador de notificações não lidas
  const unreadCount = notifications.filter((n) => !n.viewed).length;

  const handleNotificationClick = (id: string) => {
    const notification = notifications.find((n) => n.id === id)!; 
    if (notification && !notification.viewed) {
      setViewed(notification);
      atualizar();
    }
    closePopover();
    if (notification?.link_url) {
      navigate(notification.link_url);
    }
  };

  // handle do popover do usuário
  const [opened, setOpened] = useState(false);

  const handleEnterpriseChange = (id: string) => {
    const selected = user?.enterprises.find((e: any) => e.id === id) || null;
    setActualEnterprise(selected);
    setOpened(false);
    // window.location.reload(); // você pode remover isso se quiser uma UX mais suave
  };

  return (
    <>
      {modalOpened && (
        <UserEditModal opened onClose={closeModal} user={user} />
      )}
      <Box
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          maxWidth: '100vw',
          height: '70px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '0 8px' : '0 15px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          zIndex: 1000,
        }}
      >
        {/* Lado esquerdo: Hambúrguer e logo */}
        <Group
          align="center"
          spacing={isMobile ? 4 : 'sm'}
          noWrap
          style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
        >
          <Box style={{ width: isMobile ? '32px' : '45px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
            <ActionIcon onClick={onToggleSidebar} variant="transparent" size={isMobile ? 'md' : 'xl'}>
              <IconMenu2 size={isMobile ? 22 : 55} />
            </ActionIcon>
          </Box>
          <img
            src="/images/icon_h.png"
            alt="Logo da Empresa"
            style={{
              height: isMobile ? 24 : 40,
              maxWidth: isMobile ? 72 : undefined,
              objectFit: 'contain',
              flexShrink: 1,
            }}
          />
        </Group>

        {/* Lado direito: Search, ícones e dados do usuário */}
        <Group spacing={isMobile ? 2 : 'xs'} align="center" noWrap style={{ flexShrink: 0 }}>
          {!isMobile && (
            <TextInput
              placeholder={t('nav.search')}
              radius="xl"
              size="md"
              icon={<IconSearch size={16} />}
              style={{ width: '300px' }}
            />
          )}

          {/* Notificações com contador e popover sempre acima */}
          <Popover
            opened={popoverOpened}
            onClose={closePopover}
            position="bottom-end"
            withArrow
            width={300}
            withinPortal
            zIndex={10000}
          >
            <Popover.Target>
              <Indicator
                label={unreadCount}
                size={16}
                color="red"
                withBorder
                position="top-end"
                offset={-4}
                disabled={unreadCount === 0}
              >
                <ActionIcon variant="transparent" onClick={togglePopover} size={isMobile ? 'sm' : 'lg'}>
                  <IconBell size={isMobile ? 20 : 28} />
                </ActionIcon>
              </Indicator>
            </Popover.Target>
            <Popover.Dropdown style={{ backgroundColor: "#fafafa", border: `1px solid ${theme.colors.gray[3]}`}}>
              <ScrollArea style={{ height: 320 }}>
                {notifications.map((notification) => (
                  <UnstyledButton
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification.id)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px',
                      margin: '10px 0',
                      backgroundColor: notification.viewed ? theme.white : "rgba(48,113,91,0.2)",
                      borderRadius: theme.radius.sm,
                      border: `1px solid ${theme.colors.gray[3]}`,
                      textAlign: 'left',
                    }}
                  >
                    <Text weight={notification.viewed ? 400 : 700}>{notification.title}</Text>
                    <Text size="xs" color="dimmed" lineClamp={2}>
                      {notification.description}
                    </Text>
                    <Text size="xs" color="dimmed">{formatAppDateTime(notification.date)}</Text>
                  </UnstyledButton>
                ))}
              </ScrollArea>
            </Popover.Dropdown>
          </Popover>

          {!isMobile && (
            <ActionIcon variant="transparent" onClick={openModal}>
              <IconSettings size={28} />
            </ActionIcon>
          )}

          {/* Separador vertical */}
          {!isMobile && (
            <Box style={{ borderLeft: '1px solid #e0e0e0', height: '40px', margin: '0 10px' }} />
          )}

          {/* Dados do usuário */}
          <Popover
              opened={opened}
              onChange={setOpened}
              position="bottom-start"
              withArrow
              withinPortal
              shadow="md"
            >
              <Popover.Target>
                <UnstyledButton onClick={() => setOpened((o) => !o)} style={{ maxWidth: isMobile ? 120 : undefined }}>
                  <Group spacing={isMobile ? 4 : 'xs'} noWrap>
                    <Avatar
                      src={user ? parsePhotoUrl(user.photo) : "/images/user.png"}
                      alt="Usuário"
                      radius="xl"
                      size={isMobile ? 26 : 32}
                      style={{ flexShrink: 0 }}
                    />
                    <Box style={{ textAlign: 'left', minWidth: 0, paddingRight: isMobile ? 0 : '50px' }}>
                      {!isMobile && (
                        <>
                          <Text size="sm" weight={500}>
                            {user ? user.name : "Nome do Usuário"}
                          </Text>
                          <Text size="xs" color="dimmed">
                            {user?.cargo || "Cargo do Usuário"}{" "}
                            {actualEnterprise ? `[${actualEnterprise.legal_name}]` : ""}
                          </Text>
                        </>
                      )}
                      {isMobile && actualEnterprise && (
                        <Text size="xs" weight={500} truncate maw={80}>
                          {actualEnterprise.legal_name}
                        </Text>
                      )}
                    </Box>
                  </Group>
                </UnstyledButton>
              </Popover.Target>

              <Popover.Dropdown
                style={{
                  width: '200px',
                  minWidth: '200px',
                  maxWidth: '200px',
                  boxSizing: 'border-box',
                  zIndex: 9999,
                }}
              >
                <Text size="xs" weight={500} mb={5}>
                  {t('nav.selectEnterprise')}
                </Text>
                <Divider my={5} />
                {user?.enterprises?.map((enterprise: any) => (
                  <UnstyledButton
                    key={enterprise.id}
                    onClick={() => handleEnterpriseChange(enterprise.id)}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor:
                        actualEnterprise?.id === enterprise.id ? '#f1f3f5' : 'transparent',
                      width: '100%',
                      textAlign: 'left',
                    }}
                  >
                    <Text size="sm">{enterprise.legal_name}</Text>
                  </UnstyledButton>
                ))}
                {isMobile && (
                  <>
                    <Divider my="sm" />
                    <UnstyledButton
                      onClick={() => {
                        setOpened(false);
                        openModal();
                      }}
                      style={{
                        padding: '8px',
                        borderRadius: '4px',
                        width: '100%',
                        textAlign: 'left',
                      }}
                    >
                      <Group spacing="xs" noWrap>
                        <IconSettings size={18} stroke={1.5} />
                        <Text size="sm">{t('nav.settings')}</Text>
                      </Group>
                    </UnstyledButton>
                  </>
                )}
                <Divider my="sm" />
                <UnstyledButton
                  onClick={() => {
                    setOpened(false);
                    logout();
                  }}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  <Group spacing="xs" noWrap>
                    <IconLogout size={18} stroke={1.5} />
                    <Text size="sm">{t('nav.logOut')}</Text>
                  </Group>
                </UnstyledButton>
              </Popover.Dropdown>
            </Popover>
        </Group>
      </Box>
    </>
  );
}

